<template>
  <div class="primary-menu">
    <div class="icons">
      <div v-for="(icon, index) in icons" :key="index">
        <a
          :id="`${icon.id}`"
          :key="`${icon.id}`"
          :href="
            index === 2 && latestChecklistId
              ? `${icon.url}?checklistId=${latestChecklistId}`
              : icon.url
          "
          :class="`icon-wrapper ${
            index >= numberItemTop ? 'icon-hidden border-icons' : ''
          } ${hoverMenuIndex === index ? 'active-hover' : ''}`"
          @click="(e) => selectIcon(e, index, icon)"
          @mouseover="selectIconOnHover(index)"
          @mouseleave="unHoveredIcon(index)"
        >
          <component
            :is="icon.icon"
            v-if="index !== 3"
            :class="`${icon.isSmall ? 'icon-small' : 'icon'} ${
              selectedIconIndex === index ? 'active' : ''
            } ${hoverMenuIndex === index ? 'active-hover' : ''} ${
              index >= numberItemTop ? 'md:tw-hidden' : ''
            }`"
            :alt="icon.alt"
          />
          <component
            :is="icon.icon"
            v-else
            :class="`click-none !tw-filter-none ${
              icon.isSmall ? 'icon-small' : 'icon'
            } ${selectedIconIndex === index ? 'active ' : ''} ${
              index >= numberItemTop ? 'md:tw-hidden' : ''
            }`"
            :alt="icon.alt"
            :color="`${
              selectedIconIndex === 3 || iconHover ? '#387DFF' : '#211F58'
            }`"
            @mouseover="iconHover = true"
            @mouseleave="iconHover = false"
          />
        </a>
        <b-popover
          v-if="index !== hoverMenuIndex || !isMenuOpen"
          :target="`${icon.id}`"
          triggers="hover"
          placement="right"
          custom-class="custom-actions-popover"
          :offset="5"
          :delay="{
            show: 0,
            hide: 100,
          }"
          @show="true"
        >
          <div>{{ icon.sectionTitle }}</div>
        </b-popover>
      </div>
    </div>
    <div class="icons icon-setting icons-bottom">
      <span
        v-for="(icon, index) in iconGroupBottom"
        :key="index"
        class="icon-wrapper border-icons"
        @click="() => selectIcon(index + numberItemTop)"
        @mouseover="selectIconOnHover"
        @mouseleave="unHoveredIcon(index + numberItemTop)"
      >
        <component
          :is="icon.icon"
          :class="`${icon.isSmall ? 'icon-small' : 'icon'} ${
            selectedIconIndex === index + numberItemTop ? 'active' : ''
          }`"
          :alt="icon.alt"
        />
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { env } from "@/env";
import store from "@/store";
import { routers } from "@/utils/constants";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { Ref, ref } from "vue";
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { useRoute } from "vue-router";

export type PrimaryMenuIcon = {
  id: string;
  icon: any;
  alt: string;
  isSmall?: boolean;
  sectionTitle: string;
  sectionSubTitle?: string;
  url?: string;
  onSelect?(): void;
};
@Component({
  emits: [
    "onSelectedIconIndexChange",
    "set-hovered-index",
    "hovered-icon",
    "toggleMenu",
    "clicked-active",
  ],
  components: {},
})
export default class PrimaryMenu extends Vue {
  @Prop({ required: true }) readonly icons: Array<PrimaryMenuIcon> = [];
  @Prop({ default: -1 }) readonly hoverMenuIndex: number = -1;

  private timeOutRef: any | number | null = null;
  public numberItemTop = 9;
  public route = useRoute();
  public notGetChecklistPath = [
    routers.assessChecklist,
    routers.assessCreate,
    routers.assessRecommend,
    routers.shareProfile,
    routers.assessVendor,
  ];

  get primaryMenuIndex() {
    return store.state.Navigation.primaryMenuIndex;
  }

  get getLatestChecklistDetail() {
    return store.getters["Recommend/getLatestChecklistDetail"].checklistId
      ? store.getters["Recommend/getLatestChecklistDetail"]
      : store.state.Assessment.checklistAssessment[0];
  }

  async mounted() {
    this.$emit("onSelectedIconIndexChange", this.selectedIconIndex);
    this.iconGroupTop = this.icons.slice(0, this.numberItemTop);
    this.iconGroupBottom = this.icons.slice(this.numberItemTop, 11);
    this.selectedIconIndex = store.state.Navigation.primaryMenuIndex;
    eventEmitter.on(EmitterEvents.SET_PRIMARY_MENU_INDEX, (index) => {
      if (typeof index === "number") {
        this.selectedIconIndex = index;
      }
    });

    const isGetChecklist = this.notGetChecklistPath.some((path) =>
      this.route.path.includes(path),
    );

    if (!isGetChecklist) {
      const lastesChecklist = await store.dispatch(
        "Recommend/fetchChecklistData",
      );

      if (
        lastesChecklist?.latestChecklist &&
        lastesChecklist?.latestChecklist.checklistId
      ) {
        this.latestChecklistId = lastesChecklist.latestChecklist.checklistId;
      }
    } else {
      // this.latestChecklistId = this.getLatestChecklistDetail.checklistId;
    }
  }

  unmounted() {
    eventEmitter.off(EmitterEvents.SET_PRIMARY_MENU_INDEX);
  }

  @Watch("selectedIconIndex")
  onSelectedIconIndexChange() {
    this.$emit("onSelectedIconIndexChange", this.selectedIconIndex);
  }

  @Watch("primaryMenuIndex")
  onChange() {
    this.selectedIconIndex = this.primaryMenuIndex;
  }

  @Watch("getLatestChecklistDetail")
  updateLatestChecklistId() {
    this.latestChecklistId = this.getLatestChecklistDetail?.checklistId;
  }

  public selectedIconIndex: number | Ref = ref(0);
  public latestChecklistId: number | Ref = ref(null);
  public iconHover: Ref<boolean> = ref(false);
  public iconGroupTop: Array<PrimaryMenuIcon> = [];
  public iconGroupBottom: Array<PrimaryMenuIcon> = [];

  get isMenuOpen() {
    return store.state.Navigation.isMenuOpen;
  }

  selectIcon(event, index: number, icon: PrimaryMenuIcon) {
    event.preventDefault();
    if (icon.onSelect) {
      return icon.onSelect();
    }
    this.clearTimeOut();
    this.$emit("toggleMenu", index);
    if (this.selectedIconIndex === index) this.$emit("clicked-active");
    else this.selectedIconIndex = index;
  }

  // emit hover after more than X seconds
  selectIconOnHover(index: any) {
    this.clearTimeOut();
    this.$emit("set-hovered-index", index);
    if (this.isMenuOpen) return;
    this.timeOutRef = setTimeout(() => {
      this.$emit("hovered-icon", index);
    }, env.primaryMenuHoverDelayMS);
  }

  // clear timeout when mouse leaves
  clearTimeOut() {
    if (this.timeOutRef) {
      clearTimeout(this.timeOutRef);
      this.timeOutRef = null;
    }
  }

  unHoveredIcon(index: any) {
    this.clearTimeOut();
    this.$emit("set-hovered-index", null);
  }
}
</script>

<style scoped>
:deep(.custom-actions-popover .popover-arrow) {
  height: 7px;
  width: 7px;
  left: -4px;
  background-color: #e4f1ff;
  margin-top: -2.5px;
  transform: rotate(45deg);
}

:deep(.custom-actions-popover .popover-arrow)::before,
:deep(.custom-actions-popover .popover-arrow)::after {
  display: none !important;
}

:deep(.popover-body) {
  padding: 3px 5px !important;
  line-height: 130%;
  color: var(--Brand-Navy, #211f58);
  font-family: Poppins;
  font-size: 11px;
  font-weight: 500;
}
:deep(.popover) {
  top: 5px;
  background-color: #e4f1ff;
  border-radius: 4px;
  border: 1px solid #e4f1ff !important;
  box-shadow: none;
}
.primary-menu {
  width: 61px;
  padding: 18px 12px;
  height: 100%;
  border: none;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background: var(--UI-white, #fff);
  box-shadow: 5px 0px 15px 0px rgba(167, 167, 167, 0.1);
  border-right: 1px solid #eff0f1;
}

.primary-menu .icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
}

.primary-menu .footer_icons {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.primary-menu .icons .icon-wrapper {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 100%;
}

.primary-menu .icons .icon-wrapper.icon-hidden {
  display: none !important;
}

.primary-menu .border-icons {
  border-radius: 50%;
}

.primary-menu
  .icons
  .icon-wrapper:has(
    .icon:hover,
    .icon-small:hover,
    .icon:active,
    .icon-small:active,
    .icon.active
  ):not(:has(.footer_icon, .click-none)),
.primary-menu .icons .icon-wrapper:hover:not(:has(.footer_icon)) {
  background: rgba(242, 248, 255, 1);
}

/* .primary-menu .icons .icon-wrapper.active-hover {
  background: rgba(242, 248, 255, 1);
} */

.primary-menu .icons .icon-wrapper:has(.icon.active):not(:has(.footer_icon)) {
  background: rgba(239, 240, 241, 0.5);
}

.icon-wrapper:active {
  background: rgba(228, 241, 255, 1) !important;
}

.primary-menu .icons .icon-wrapper .icon {
  width: 16px;
  height: 16px;
}

.primary-menu .icons .icon-wrapper .icon-small {
  width: 16px;
  height: 16px;
}

.active:not(:has(.click-none)) {
  filter: invert(44%) sepia(96%) saturate(3315%) hue-rotate(208deg)
    brightness(102%) contrast(101%);
}

.primary-menu .icons .icon-wrapper:hover .icon,
.primary-menu .icons .icon-wrapper:hover .icon-small:not(:has(.click-none)) {
  cursor: pointer;
  filter: invert(44%) sepia(96%) saturate(3315%) hue-rotate(208deg)
    brightness(102%) contrast(101%);
}

/* .primary-menu .icons .icon-wrapper.active-hover .icon,
.primary-menu
  .icons
  .icon-wrapper.active-hover
  .icon-small:not(:has(.click-none)) {
  cursor: pointer;
  filter: invert(44%) sepia(96%) saturate(3315%) hue-rotate(208deg)
    brightness(102%) contrast(101%);
} */

.primary-menu .icons .icon-wrapper:has(.footer_icon.active) {
  width: 36px;
  height: 37px;
  background: rgb(3, 69, 189, 0.07);
  border-radius: 50%;
}

.primary-menu .footer_icons .icon-wrapper:hover .footer_icon {
  cursor: pointer;
  /* filter: invert(44%) sepia(96%) saturate(3315%) hue-rotate(208deg)
    brightness(102%) contrast(101%); */
}

.primary-menu .footer_icons .icon-wrapper:hover .footer_icon::after {
  content: "ssss";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgb(3, 69, 189, 0.07);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0.02em;
  text-align: center;
  text-transform: uppercase;
  z-index: 1;
}

.primary-menu .footer_icons .icon-wrapper .footer_icon.active {
  cursor: pointer;
  filter: none;
}

.primary-menu
  .icons
  .icon-wrapper-setting:has(
    .icon:hover,
    .icon-small:hover,
    .icon-small.active,
    .icon.active
  ) {
  background: none;
}
@media (max-width: 767px) {
  .primary-menu {
    width: 100%;
    height: 55px;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    flex-direction: row;
    justify-content: space-between;
    box-shadow: none;
  }

  .primary-menu .icons {
    flex-direction: row;
    gap: unset;
    justify-content: space-around;
    margin-top: 0;
    width: 100%;
  }

  .primary-menu .icons .icon-wrapper {
    width: 36px;
    height: 37px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .primary-menu .icons .icon-wrapper.icon-hidden {
    display: flex !important;
  }

  .primary-menu .icon-setting {
    display: none;
  }
}
</style>
